import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useTranslation } from 'react-i18next';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { GetIncidencesQueryDtoStatusEnum } from '../maintenance-orders/maintenance-order-list';

interface AssignMaintenanceOrderModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  incidentId: string;
  deviceSerial?: string | null;
  onAssignedSuccess?: (incidentId: string) => void | Promise<void>;
}

type MaintenanceOrderOption = {
  label: string;
  value: string;
};

const normalizeSerial = (value?: string | null): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const ModalAssignMaintenanceOrder: React.FC<AssignMaintenanceOrderModalProps> = ({
  visible,
  setVisible,
  incidentId,
  deviceSerial,
  onAssignedSuccess,
}) => {
  const { t } = useTranslation();

  const [orders, setOrders] = useState<MaintenanceOrderOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [missingSerial, setMissingSerial] = useState(false);

  useEffect(() => {
    if (!visible || !incidentId) return;

    let cancelled = false;

    const loadOrders = async (): Promise<void> => {
      setLoading(true);
      setMissingSerial(false);

      try {
        const [allOrdersResponse, assignedResponse, incidenceSerial] =
          await Promise.all([
            apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetAllMaintenanceOrders(GetIncidencesQueryDtoStatusEnum.Open),
            apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetMaintenanceOrdersByIncidence(
              incidentId
            ),
            deviceSerial
              ? Promise.resolve(deviceSerial)
              : apiServices.incidenceApi
                .incidenceControllerGetIncidenceDetailsById(incidentId)
                .then((response) => response.data?.device_serial),
          ]);

        if (cancelled) return;

        const targetSerial = normalizeSerial(incidenceSerial);

        if (!targetSerial) {
          setMissingSerial(true);
          setOrders([]);
          return;
        }

        // Las que ya cuelgan de esta incidencia no se vuelven a ofrecer: el
        // endpoint de asociación no deduplica y crearía una fila repetida.
        const assignedIds = new Set(
          (assignedResponse.data ?? [])
            .map((order) => order.id)
            .filter(Boolean)
        );

        const options = (allOrdersResponse.data ?? [])
          .filter(
            (order) =>
              order.id &&
              !assignedIds.has(order.id) &&
              // Sólo el mismo equipo. Una orden sin serie no se puede
              // verificar, así que se descarta.
              normalizeSerial(order.device_serial_number) === targetSerial
          )
          .map((order) => ({
            label: order.code
              ? `${order.code}${order.description ? ` — ${order.description}` : ''}`
              : order.id,
            value: order.id,
          }));

        setOrders(options);
      } catch (error) {
        console.error('Error loading maintenance orders:', error);
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, [visible, incidentId, deviceSerial]);

  const emptyMessage = useMemo(() => {
    if (loading) return t('loading');
    if (missingSerial) return t('pages.incidents.assignOrder_noDevice');

    return t('pages.incidents.assignOrder_noneAvailable');
  }, [loading, missingSerial, t]);

  const onHide = (): void => {
    setSelectedIds([]);
    setOrders([]);
    setVisible(false);
  };

  const onSubmit = async (): Promise<void> => {
    if (selectedIds.length === 0) return;

    setSaving(true);

    try {
      await apiServices.incidenceApi.incidenceControllerAssignMaintenanceOrders(
        selectedIds,
        incidentId
      );

      await onAssignedSuccess?.(incidentId);
      onHide();
    } catch (error) {
      console.error('Error assigning maintenance orders:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={<h2>{t('pages.incidents.assignOrder')}</h2>}
      style={{ width: '40rem' }}
      breakpoints={{ '960px': '80vw', '640px': '95vw' }}
    >
      <label htmlFor="assign-maintenance-order-id" className="block mb-2">
        {t('pages.incidents.titleOrder')}
        <span className="text-red-500 ml-1" aria-hidden="true">
          *
        </span>
      </label>

      <MultiSelect
        id="assign-maintenance-order-id"
        className="w-full"
        value={selectedIds}
        options={orders}
        onChange={(e) => setSelectedIds(e.value as string[])}
        optionLabel="label"
        optionValue="value"
        filter
        display="chip"
        disabled={loading || missingSerial}
        placeholder={
          loading ? t('loading') : t('pages.incidents.titleOrder')
        }
        emptyMessage={emptyMessage}
        emptyFilterMessage={t('noResults')}
      />

      <small className="block mt-2 text-600">
        {t('pages.incidents.assignOrder_sameDeviceHint')}
      </small>

      <div className="flex align-items-center justify-content-end mt-4">
        <Button
          id="button-assign-maintenance-order"
          severity="secondary"
          icon="pi pi-save"
          label={t('save')}
          className="p-button-raised"
          type="button"
          onClick={onSubmit}
          loading={saving}
          disabled={selectedIds.length === 0}
        />
      </div>
    </Dialog>
  );
};

export default ModalAssignMaintenanceOrder;